import { useEffect } from "react";
import { hasPhysicsWorker } from "../../physics/workerHandle";
import { usePhysicsStore } from "../../stores/physicsStore";
import { useSceneStore } from "../../stores/sceneStore";
import { subscribeFloorAppearanceOptions } from "../../utils/floorAppearanceOptions";
import { getStandardFloorHField } from "../../utils/terrainFloor";
import {
  getTerrainVisualizationOptions,
  subscribeTerrainVisualizationOptions,
  syncTerrainVisualizationForStandardFloorPresence,
} from "../../utils/terrainVisualizationOptions";
import {
  applySceneHdriEnvironment,
  applyStandardFloorAppearance,
  applyStandardFloorTerrain,
  DEFAULT_SCENE_FLOOR_SIZE,
} from "../sceneDefaults";
import { subscribeSceneHdriBackgroundVisibility } from "../../utils/sceneHdriBackgroundVisibility";
import { syncGeomTransformsForBodyPoseGroup } from "../syncGeomTransformsFromStore";

export function useSceneFloorSubscriptions({ sceneObjectsRef, sceneRef }) {
  useEffect(() => {
    const unsubscribe = useSceneStore.subscribe((state, previousState) => {
      if (!sceneRef.current) return;
      if (state.hfields !== previousState.hfields) {
        const floor = sceneRef.current.getObjectByName("sceneFloor");
        if (floor?.isMesh) {
          const hfieldDef = getStandardFloorHField(state.hfields);
          syncTerrainVisualizationForStandardFloorPresence(Boolean(hfieldDef));
          applyStandardFloorTerrain(
            sceneRef.current,
            DEFAULT_SCENE_FLOOR_SIZE,
            hfieldDef || null,
            getTerrainVisualizationOptions()
          );
        }
      }
      if (state.background !== previousState.background) {
        applySceneHdriEnvironment(sceneRef.current);
      }
    });
    const h0 = useSceneStore.getState().hfields;
    syncTerrainVisualizationForStandardFloorPresence(Boolean(getStandardFloorHField(h0)));
    return unsubscribe;
  }, [sceneRef]);

  useEffect(() => {
    const unsubscribe = useSceneStore.subscribe((state, previousState) => {
      if (state.bodies === previousState.bodies) return;
      if (usePhysicsStore.getState().isRunning && hasPhysicsWorker()) return;
      const objectsMap = sceneObjectsRef.current || {};
      const bodies = state.bodies || {};
      for (const obj of Object.values(objectsMap)) {
        if (!obj) continue;
        const bodyUuid = obj.userData?.bodyUuid;
        if (!bodyUuid) continue;
        syncGeomTransformsForBodyPoseGroup(obj, bodies);
      }
    });
    return unsubscribe;
  }, [sceneObjectsRef]);

  useEffect(() => {
    return subscribeTerrainVisualizationOptions(() => {
      if (!sceneRef.current) return;
      const floor = sceneRef.current.getObjectByName("sceneFloor");
      if (!floor?.isMesh) return;
      const hfieldDef = getStandardFloorHField(useSceneStore.getState().hfields);
      applyStandardFloorTerrain(
        sceneRef.current,
        DEFAULT_SCENE_FLOOR_SIZE,
        hfieldDef || null,
        getTerrainVisualizationOptions()
      );
    });
  }, [sceneRef]);

  useEffect(() => {
    return subscribeFloorAppearanceOptions(() => {
      if (!sceneRef.current) return;
      applyStandardFloorAppearance(sceneRef.current);
      applySceneHdriEnvironment(sceneRef.current);
    });
  }, [sceneRef]);

  useEffect(() => {
    return subscribeSceneHdriBackgroundVisibility(() => {
      if (!sceneRef.current) return;
      applySceneHdriEnvironment(sceneRef.current);
    });
  }, [sceneRef]);
}
